import * as React from 'react'
import { type VariantProps } from 'class-variance-authority'
import { mtc, TextColors, TypographyStyles } from '../../../utils/style-helpers'
import {
  alertVariants,
  alertTitleStyles,
  alertDescriptionStyles,
  inlineLinkStyles,
  ctaStyles,
} from './styles'
import {
  faCircleInfo,
  faCheck,
  faTriangleExclamation,
  faCircleExclamation,
  faStar,
} from '@fortawesome/pro-solid-svg-icons'
import { faArrowRight, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { Attributes, IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { Icon } from '../icon'
import { Text } from '../text'
import { Link } from '../link'
import { Button, ButtonProps } from '../button'

interface AlertProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  title?: string
  description?: string
  customIconName?: IconDefinition
  /**
   * Icon size in px, not rem (rem ties to root font-size). Set via
   * inline style — otherwise FontAwesome's own CSS wins the cascade.
   */
  iconSize?: string
  dismissible?: boolean
  children?: React.ReactNode
  permanentDismissalKey?: string
  onClose?: () => void
  inlineLinkClassName?: string
  inlineLinkTrackMessage?: string
  inlineLinkTrackAttributes?: Attributes
  inlineLinkNewPage?: boolean
  inlineLinkText?: string
  inlineLinkHref?: string
  inlineLinkOnClick?: () => void
  ctaText?: string
  ctaOnClick?: () => void
  ctaClassName?: string
  ctaProps?: ButtonProps
}

type AlertVariant = NonNullable<AlertProps['variant']>

const iconMap = {
  default: { icon: faCircleInfo, color: 'var(--color-moss-200)' },
  success: { icon: faCheck, color: 'var(--color-green-200)' },
  warning: { icon: faTriangleExclamation, color: 'var(--color-yellow-200)' },
  error: { icon: faCircleExclamation, color: 'var(--color-red-200)' },
  priority: { icon: faStar, color: 'var(--color-orange-200)' },
}

const colorMap = {
  default: 'neutral-600',
  success: 'green-100',
  warning: 'yellow-100',
  error: 'red-100',
  priority: 'orange-200',
}

export const Alert: React.FC<AlertProps> = ({
  className,
  variant = 'default',
  title,
  description,
  customIconName,
  iconSize = '16px',
  dismissible = false,
  children,
  permanentDismissalKey,
  onClose = () => {},
  inlineLinkClassName,
  inlineLinkTrackMessage,
  inlineLinkTrackAttributes,
  inlineLinkNewPage,
  inlineLinkText,
  inlineLinkHref,
  inlineLinkOnClick,
  ctaText,
  ctaOnClick,
  ctaClassName,
  ctaProps,
  ...props
}) => {
  const [visible, setVisible] = React.useState(
    permanentDismissalKey ? !localStorage.getItem(permanentDismissalKey) : true
  )
  const { icon: defaultIcon, color } = iconMap[variant as AlertVariant]
  const textColor = colorMap[variant as AlertVariant]

  if (!visible) return null

  const handleClose = () => {
    setVisible(false)
    if (permanentDismissalKey) {
      localStorage.setItem(permanentDismissalKey, 'true')
    }
    onClose()
  }

  return (
    <div
      role="alert"
      className={mtc(
        alertVariants({ variant }),
        `${className} flex items-start gap-4`
      )}
      {...props}
    >
      <Icon
        icon={customIconName ? customIconName : defaultIcon}
        className="mt-0.5 shrink-0"
        style={{ width: iconSize, height: iconSize, color }}
      />
      <div className="flex flex-col md:flex-row md:justify-between md:items-center flex-grow-1 gap-3">
        <div className="flex flex-col flex-grow">
          {title && (
            <Text
              variant={TypographyStyles.LABEL_MD}
              className={mtc(alertTitleStyles({ variant }))}
            >
              {title}
            </Text>
          )}
          {children}
          {description && (
            <Text
              variant={TypographyStyles.BODY_SM}
              color={textColor as TextColors}
              className={mtc(alertDescriptionStyles())}
            >
              {description}
              {inlineLinkHref && (
                <Link
                  href={inlineLinkHref}
                  className={mtc(inlineLinkStyles(), inlineLinkClassName)}
                  newPage={inlineLinkNewPage}
                  trackMessage={inlineLinkTrackMessage}
                  trackAttributes={inlineLinkTrackAttributes}
                >
                  {inlineLinkText}
                  <Icon
                    className="pl-[2px]"
                    backgroundClassName="mr-0"
                    icon={faArrowRight}
                  />
                </Link>
              )}
              {inlineLinkOnClick && (
                <Button
                  variant="link"
                  className={mtc(inlineLinkStyles(), inlineLinkClassName)}
                  onClick={inlineLinkOnClick}
                  size="sm"
                >
                  {inlineLinkText}
                  <Icon
                    className="pl-[2px]"
                    backgroundClassName="mr-0"
                    icon={faArrowRight}
                  />
                </Button>
              )}
            </Text>
          )}
        </div>
        {ctaOnClick && (
          <Button
            variant="secondary"
            className={mtc(ctaStyles(), ctaClassName)}
            onClick={ctaOnClick}
            size="sm"
            {...ctaProps}
          >
            {ctaText}
          </Button>
        )}
      </div>
      {dismissible && (
        <Icon
          icon={faTimes}
          className="h-4 w-4 cursor-pointer"
          onClick={handleClose}
          color="text-neutral-500"
        />
      )}
    </div>
  )
}
Alert.displayName = 'Alert'
